/**
 * Jun-2026 (User-Wunsch) — Org-Cockpit-Panel „Filialen-Logins".
 * Zeigt je Filiale den Einladungs-/Login-Status und hebt Filialen ohne
 * aktiven Login hervor. Sammelaktion „Alle erinnern" sendet allen offenen
 * Filialen erneut eine Einladungs-Mail.
 */
import { useEffect, useState, useCallback } from "react";
import { Loader2, BellRing, CheckCircle2, Clock, AlertTriangle, KeyRound, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { usePersistentToggle } from "../../lib/usePersistentToggle";

const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return iso; }
};

const STATE = {
    accepted: { icon: CheckCircle2, cls: "text-emerald-600", label: (s) => `Login aktiv · seit ${fmt(s.password_set_at)}` },
    active: { icon: CheckCircle2, cls: "text-emerald-600", label: () => "Login aktiv" },
    invited: { icon: Clock, cls: "text-amber-600", label: (s) => `Eingeladen am ${fmt(s.invited_at)} · noch offen` },
    expired: { icon: AlertTriangle, cls: "text-red-600", label: (s) => `Einladung vom ${fmt(s.invited_at)} abgelaufen` },
    none: { icon: AlertTriangle, cls: "text-gray-400", label: () => "Noch nicht eingeladen" },
};

export default function BranchLoginPanel() {
    const [open, setOpen] = usePersistentToggle("org-branch-login-open", false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [reminding, setReminding] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/org/branches/invite-overview")
            .then((r) => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const remindAll = async () => {
        setReminding(true);
        try {
            const r = await api.post("/org/branches/remind-all-logins");
            toast.success(`${r.data?.reminded ?? 0} Filiale(n) erinnert.${r.data?.skipped_no_email ? ` ${r.data.skipped_no_email} ohne E-Mail übersprungen.` : ""}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erinnerung fehlgeschlagen.");
        } finally { setReminding(false); }
    };

    if (!data && !loading) return null;
    const pending = data?.pending_login_count || 0;
    const rows = data?.branches || [];

    return (
        <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="org-branch-login-panel">
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
                <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left min-w-0 flex-1"
                    data-testid="org-branch-login-toggle" aria-expanded={open}>
                    <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
                    <KeyRound className="w-4 h-4 text-[#2563EB] shrink-0" />
                    <span className="font-bold text-sm text-[#111827]">Filialen-Logins</span>
                    {pending > 0 && (
                        <span className="text-[11px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid="org-branch-login-pending-badge">
                            {pending} ohne aktiven Login
                        </span>
                    )}
                </button>
                {pending > 0 && (
                    <button type="button" onClick={remindAll} disabled={reminding}
                        className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-bold bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-3 py-1.5 rounded-sm disabled:opacity-60"
                        data-testid="org-branch-login-remind-all">
                        {reminding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                        Alle erinnern
                    </button>
                )}
            </div>
            {open && (
                <div className="divide-y divide-gray-50" data-testid="org-branch-login-list">
                    {loading && <div className="p-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
                    {!loading && rows.length === 0 && <div className="p-4 text-xs text-gray-500">Keine Filialen verknüpft.</div>}
                    {rows.map((b) => {
                        const st = b.invite_status || {};
                        const cfg = STATE[st.state] || STATE.none;
                        const Icon = cfg.icon;
                        return (
                            <div key={b.branch_id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`org-branch-login-row-${b.branch_id}`}>
                                <Icon className={`w-4 h-4 shrink-0 ${cfg.cls}`} />
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-[#111827] truncate">
                                        {b.company_name}{b.site_code && b.site_code !== "—" ? ` · ${b.site_code}` : ""}
                                    </div>
                                    <div className="text-[11px] text-gray-500 truncate">{b.login_email || "keine E-Mail"} · {cfg.label(st)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
